import { findStrategy, findFund, findCust, findBrokerAbb } from "../../component/Methods";

export const SSANDC_ROUTER_ID = "0ea492818ab8eda55cb6bd0760647d77";
export const GET_SSANDC_FILLS_REQUEST = "get_ssandc_fills";
export const ALLOCATE_SSANDC_TRADES_REQUEST = "allocate_ssandc_trades";
export const CANCEL_SSANDC_TRADES_REQUEST = "cancel_ssandc_trades";

export function getStartOfLocalDay(date = new Date()) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

export function formatLocalDateInputValue(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export function formatLocalDateKey(date) {
  return formatLocalDateInputValue(new Date(date));
}

/** Assign per-calendar-day serial numbers (1-based) to transformed trade rows. */
export function assignDailySerialNumbers(rows) {
  const tradesByDate = {};
  rows.forEach((trade) => {
    if (!trade?.orderDate) return;
    const key = formatLocalDateKey(trade.orderDate);
    if (!tradesByDate[key]) tradesByDate[key] = [];
    tradesByDate[key].push(trade);
  });
  return Object.values(tradesByDate).flatMap((dateTrades) =>
    dateTrades.map((trade, index) => ({ ...trade, serialNumber: index + 1 }))
  );
}

export function getLastOrder(tradeObj) {
  if (!tradeObj?.orders?.length) return null;
  return tradeObj.orders[tradeObj.orders.length - 1];
}

export function abbreviationIncludesSsandc(abbreviation) {
  return (abbreviation || "").toLowerCase().includes("ss&c");
}

export function routerDisplayIncludesSsandc(routerDisplay) {
  return (routerDisplay || "").toLowerCase().includes("ss&c");
}

export function getSsandcRouterId(routes) {
  return (
    routes?.find((r) =>
      abbreviationIncludesSsandc((r?.abbreviation || "").trim())
    )?.routerId ?? SSANDC_ROUTER_ID
  );
}

export function findRouteByRouterId(routes, routerId) {
  if (routerId == null || routerId === "" || !Array.isArray(routes)) return null;
  return (
    routes.find(
      (r) =>
        r?.routerId === routerId || String(r?.routerId) === String(routerId)
    ) || null
  );
}

export function isRouterIdSet(routerId) {
  return (
    routerId != null &&
    routerId !== "" &&
    String(routerId).trim() !== ""
  );
}

/** SS&C routed (router set and matches SS&C); allocation may already exist. */
export function isSsandcRoutedOnlySelectionItem(t, routes) {
  const rid = t?.tradeObject?.router?.router;
  if (!isRouterIdSet(rid)) return false;
  const match = findRouteByRouterId(routes, rid);
  return abbreviationIncludesSsandc(match?.abbreviation);
}

/** SS&C routed with no allocation yet (eligible to allocate). */
export function isSsandcRoutedSelectionItem(t, routes) {
  if (t?.tradeObject?.allocation != null) return false;
  return isSsandcRoutedOnlySelectionItem(t, routes);
}

export function getTradeIdsFromSelection(items) {
  return items.map((trade) => String(trade?.tradeId));
}

export function getEligibleTradeIdsFromSelection(
  selectedTrades,
  routes,
  isEligibleFn
) {
  if (!Array.isArray(selectedTrades) || selectedTrades.length === 0) return [];
  return selectedTrades
    .filter((t) => isEligibleFn(t, routes))
    .map((t) => String(t.tradeId));
}

export function isLocalToday(d) {
  const s = getStartOfLocalDay();
  return (
    d.getFullYear() === s.getFullYear() &&
    d.getMonth() === s.getMonth() &&
    d.getDate() === s.getDate()
  );
}

/** UTC calendar-day match for order timestamps vs selected local date. */
export function isOrderOnSelectedDateUtc(orderDate, selectedDate) {
  if (!orderDate || !selectedDate) return false;
  const selectedDateUTC = Date.UTC(
    selectedDate.getFullYear(),
    selectedDate.getMonth(),
    selectedDate.getDate()
  );
  const selectedDateStartUTC = new Date(selectedDateUTC);
  const selectedDateEndUTC = new Date(
    selectedDateUTC + 24 * 60 * 60 * 1000 - 1
  );
  const order = new Date(orderDate);
  const orderDateUTC = Date.UTC(
    order.getUTCFullYear(),
    order.getUTCMonth(),
    order.getUTCDate(),
    order.getUTCHours(),
    order.getUTCMinutes(),
    order.getUTCSeconds(),
    order.getUTCMilliseconds()
  );
  return (
    orderDateUTC >= selectedDateStartUTC.getTime() &&
    orderDateUTC <= selectedDateEndUTC.getTime()
  );
}

export function getDistributionLegsForFilter(item) {
  if (item.distributionLegs?.length > 0) {
    return item.distributionLegs.map((leg) => ({
      fund: leg.fundName,
      strategy: leg.strategyName,
      custodian: leg.custodianName,
      account: leg.account,
    }));
  }
  if (item.childTrades?.length > 0) {
    return item.childTrades.map((child) => ({
      fund: child.fund,
      strategy: child.strategy,
      custodian: child.custodian,
      account: child.account,
      sharesOrdered: child.sharesOrdered,
      sharesFilled: child.sharesFilled,
      filledPercent: child.filledPercent,
    }));
  }
  return [];
}

export function getFundFilterValue(row) {
  if (!row) return "";
  if (row.distributionType === "P") {
    const legs = getDistributionLegsForFilter(row);
    if (legs.length > 0) {
      return [
        ...new Set(
          legs
            .map((leg) => leg.fund)
            .filter((value) => value && value !== "N/A" && value !== "Multiple")
        ),
      ].join(" | ");
    }
  }
  return row.fund ?? "";
}

export function getStrategyFilterValue(row) {
  if (!row) return "";
  if (row.distributionType === "P") {
    const legs = getDistributionLegsForFilter(row);
    if (legs.length > 0) {
      return [
        ...new Set(
          legs
            .map((leg) => leg.strategy)
            .filter((value) => value && value !== "N/A" && value !== "Multiple")
        ),
      ].join(" | ");
    }
  }
  return row.strategy ?? "";
}

/** Unique strategy names for a trade row (uses distribution legs for parent trades). */
export function collectUniqueStrategiesFromTrade(trade) {
  const strategies = new Set();
  const legs = getDistributionLegsForFilter(trade);
  if (trade?.distributionType === "P" && legs.length > 0) {
    legs.forEach((leg) => {
      if (leg.strategy && leg.strategy !== "N/A" && leg.strategy !== "Multiple") {
        strategies.add(leg.strategy);
      }
    });
    return strategies;
  }
  if (trade?.strategy && trade.strategy !== "N/A" && trade.strategy !== "Multiple") {
    strategies.add(trade.strategy);
  }
  return strategies;
}

export function collectUniqueStrategiesFromTrades(trades) {
  const strategies = new Set();
  (trades || []).forEach((trade) => {
    collectUniqueStrategiesFromTrade(trade).forEach((strategy) => strategies.add(strategy));
  });
  return strategies;
}

export function tradeRowMatchesFund(trade, fundName) {
  if (!fundName) return true;
  if (trade?.fund === fundName) return true;
  if (trade?.distributionType === "P") {
    return getDistributionLegsForFilter(trade).some((leg) => leg.fund === fundName);
  }
  return false;
}

export function tradeRowMatchesStrategy(trade, strategyName) {
  if (!strategyName) return true;
  if (trade?.strategy === strategyName) return true;
  if (trade?.distributionType === "P") {
    return getDistributionLegsForFilter(trade).some((leg) => leg.strategy === strategyName);
  }
  return false;
}

export function collectUniqueTradeFieldValues(rows, field) {
  const valueSet = new Set();
  (rows || []).forEach((trade) => {
    if (
      trade?.[field] &&
      trade[field] !== "N/A" &&
      trade[field] !== "Multiple"
    ) {
      valueSet.add(trade[field]);
    }
  });
  return Array.from(valueSet).sort();
}

export function filterTradeRowsByDropdownFilters(
  rows,
  { selectedEntity, selectedStrategy, selectedTicker }
) {
  let data = rows;
  if (selectedEntity) {
    data = data.filter((item) => item.fund === selectedEntity);
  }
  if (selectedStrategy) {
    data = data.filter((item) => item.strategy === selectedStrategy);
  }
  if (selectedTicker) {
    data = data.filter((item) => item.ticker === selectedTicker);
  }
  return data;
}

export function sortTickersLettersBeforeNumbers(tickers) {
  return [...tickers].sort((a, b) => {
    const aIsNumber = /^\d/.test(a);
    const bIsNumber = /^\d/.test(b);
    if (aIsNumber && !bIsNumber) return 1;
    if (!aIsNumber && bIsNumber) return -1;
    return a.localeCompare(b);
  });
}

export function collectUniqueTickersFromTrades(rows) {
  return sortTickersLettersBeforeNumbers(collectUniqueTradeFieldValues(rows, "ticker"));
}

export function getTradeBlotterTickerOptions(
  dateFilteredRows,
  { searchTerm, selectedEntity, selectedStrategy, flatChildView }
) {
  if (flatChildView) {
    const flattened = flattenTradeRowsForGrid(dateFilteredRows);
    const filtered = filterTradeRowsByDropdownFilters(flattened, {
      selectedEntity,
      selectedStrategy,
      selectedTicker: "",
    });
    const searched = filterTradeRowsBySearch(filtered, searchTerm, true);
    return collectUniqueTickersFromTrades(searched);
  }

  const filtered = filterTradeRowsByDropdownFilters(dateFilteredRows, {
    selectedEntity,
    selectedStrategy,
    selectedTicker: "",
  });
  const searched = filterTradeRowsBySearch(filtered, searchTerm, false);
  return collectUniqueTickersFromTrades(searched);
}

function appendTradeSearchValue(values, value) {
  if (value === undefined || value === null) return;
  const text = String(value).trim();
  if (!text || text === "N/A") return;
  values.push(text);
}

/** Collect all user-visible / searchable text for a grid row. */
export function collectTradeRowSearchValues(item) {
  const values = [];

  appendTradeSearchValue(values, item.timestamp);
  appendTradeSearchValue(values, item.ticker);
  appendTradeSearchValue(values, item.fund);
  appendTradeSearchValue(values, item.strategy);
  appendTradeSearchValue(values, getFundFilterValue(item));
  appendTradeSearchValue(values, getStrategyFilterValue(item));
  appendTradeSearchValue(values, item.state);
  appendTradeSearchValue(values, item.side);
  appendTradeSearchValue(values, item.quantity);
  appendTradeSearchValue(values, item.filledShares);
  appendTradeSearchValue(values, item.filledPercent);
  appendTradeSearchValue(values, item.orderType);
  appendTradeSearchValue(values, item.pricePerShare);
  appendTradeSearchValue(values, item.commPerShare);
  appendTradeSearchValue(values, item.custodian);
  appendTradeSearchValue(values, item.broker);
  appendTradeSearchValue(values, item.routerDisplay);
  appendTradeSearchValue(values, item.routeStatus);
  appendTradeSearchValue(values, item.routable);
  appendTradeSearchValue(values, item.options);
  appendTradeSearchValue(values, item.strikePrice);
  appendTradeSearchValue(values, item.expirationDate);
  appendTradeSearchValue(values, item.account);
  appendTradeSearchValue(values, item.tradeId);
  appendTradeSearchValue(values, item.fundAbbreviation);
  appendTradeSearchValue(values, item.brokerAbbreviation);

  if (item.routing === 0 || item.routing === "0") {
    values.push("Auto");
  } else if (item.routing != null && item.routing !== "") {
    values.push("Manual");
  }

  if (item.distributionType === "P") {
    values.push("Parent", "P");
  } else if (item.distributionType === "C") {
    values.push("Child", "C");
  } else if (item.distributionType === "S") {
    values.push("Single", "S");
  }

  if (
    item.allocation &&
    typeof item.allocation === "object" &&
    Object.keys(item.allocation).length > 0
  ) {
    values.push("Allocated");
  }

  if (item.cancellationStatus === 0) {
    values.push("Cancel");
  }

  return values;
}

function distributionLegMatchesSearch(leg, searchTerm) {
  const q = searchTerm.toLowerCase().trim();
  const legValues = [
    leg.fund,
    leg.strategy,
    leg.custodian,
    leg.account,
    leg.sharesOrdered,
    leg.sharesFilled,
    leg.filledPercent,
  ];
  return legValues.some((value) =>
    value?.toString().toLowerCase().includes(q)
  );
}

/** General text search across row fields and aggregated parent leg values. */
export function tradeRowMatchesSearch(item, searchTerm) {
  if (!searchTerm?.trim()) return true;
  const q = searchTerm.toLowerCase().trim();
  return collectTradeRowSearchValues(item).some((field) =>
    field.toLowerCase().includes(q)
  );
}

/** Hierarchy rows: match parent/single row or any distribution leg. */
export function tradeRowOrChildMatchesSearch(item, searchTerm) {
  if (!searchTerm?.trim()) return true;
  if (tradeRowMatchesSearch(item, searchTerm)) return true;
  if (item.distributionType !== "P") return false;

  return getDistributionLegsForFilter(item).some((leg) =>
    distributionLegMatchesSearch(leg, searchTerm)
  );
}

export function filterTradeRowsBySelectedDate(rows, selectedDate) {
  if (!selectedDate) return rows;
  return rows.filter((item) => isOrderOnSelectedDateUtc(item.orderDate, selectedDate));
}

export function filterTradeRowsBySearch(rows, searchTerm, flatChildView = false) {
  if (!searchTerm?.trim()) return rows;
  if (!flatChildView) {
    return rows.filter((item) => tradeRowOrChildMatchesSearch(item, searchTerm));
  }
  return rows.filter((item) => tradeRowMatchesSearch(item, searchTerm));
}

/** Apply search, flattening first in flat view so each child row is matched individually. */
export function prepareTradeBlotterGridData(
  dateFilteredRows,
  searchTerm,
  flatChildView,
  dropdownFilters = null
) {
  const applyDropdownFilters = (rows) =>
    dropdownFilters
      ? filterTradeRowsByDropdownFilters(rows, dropdownFilters)
      : rows;

  if (!flatChildView) {
    return filterTradeRowsBySearch(
      applyDropdownFilters(dateFilteredRows),
      searchTerm,
      false
    );
  }

  const flattened = flattenTradeRowsForGrid(dateFilteredRows);
  return filterTradeRowsBySearch(
    applyDropdownFilters(flattened),
    searchTerm,
    true
  );
}

export function formatOrderTimestamp(date) {
  if (!date) return "00:00:00";
  const dateObj = new Date(date);
  const hours = String(dateObj.getHours()).padStart(2, "0");
  const minutes = String(dateObj.getMinutes()).padStart(2, "0");
  const seconds = String(dateObj.getSeconds()).padStart(2, "0");
  return `${hours}:${minutes}:${seconds}`;
}

export function parseDistributionKeyParts(distributionKey) {
  const parts = distributionKey.split("#");
  if (parts.length < 3) return null;
  return {
    account: parts[0],
    fundHash: parts[1],
    entityType: parts[2],
    strategyHash: parts[3],
  };
}

export function resolveDistributionEntities(
  distributionKey,
  { funds, strategies, custodians }
) {
  const parsed = parseDistributionKeyParts(distributionKey);
  if (!parsed) {
    return { fundName: "N/A", strategyName: "N/A", custodianName: "N/A", account: "" };
  }
  const { account, fundHash, strategyHash } = parsed;
  const fundName = findFund(fundHash, funds) || "N/A";
  const strategyName = findStrategy(strategyHash, strategies) || "N/A";
  let custodianName = "N/A";
  const selectedFund = funds.find((fund) => fund.fundId === fundHash);
  if (selectedFund?.accounts?.[account]) {
    const custodianId = selectedFund.accounts[account].custodian;
    custodianName = findCust(custodianId, custodians) || "N/A";
  }
  return { fundName, strategyName, custodianName, account };
}

/** Shared fill % / state logic for manual and auto-fill branches. */
export function resolveFilledPercentAndState(
  totalOrderedShares,
  filledShares,
  isBrokerCross
) {
  let state = "Working";
  if (totalOrderedShares === filledShares) {
    state = "Ready";
  }
  if (isBrokerCross) {
    return { state: "Ready", filledPercent: 100 };
  }
  const filledPercent =
    totalOrderedShares > 0
      ? Math.round((filledShares / totalOrderedShares) * 100)
      : 0;
  return { state, filledPercent };
}

export function resolveWorkingOrderFillMetrics(isBrokerCross) {
  if (isBrokerCross) {
    return { state: "Ready", filledPercent: 100, pricePerShare: 0, filledShares: 0 };
  }
  return { state: "Working", filledPercent: 0, pricePerShare: 0, filledShares: 0 };
}

export function resolveAggregateDistributionLabel(values) {
  const normalized = (values || [])
    .map((value) => (value == null ? "" : String(value).trim()))
    .filter((value) => value && value !== "N/A");
  const unique = [...new Set(normalized)];
  if (unique.length === 0) return "N/A";
  if (unique.length === 1) return unique[0];
  return "Multiple";
}

/** Pro-rata fill metrics for a child leg from parent fill totals. */
export function computeProRataChildFillMetrics(
  parentFilledShares,
  parentTotalQuantity,
  childQuantity,
  isBrokerCross
) {
  if (childQuantity <= 0) {
    return { filledShares: 0, filledPercent: 0, state: "Working" };
  }
  if (isBrokerCross) {
    const { state, filledPercent } = resolveFilledPercentAndState(
      childQuantity,
      childQuantity,
      true
    );
    return { filledShares: childQuantity, filledPercent, state };
  }
  if (parentTotalQuantity <= 0) {
    return { filledShares: 0, filledPercent: 0, state: "Working" };
  }
  const childFilledShares = Math.round(
    (childQuantity / parentTotalQuantity) * parentFilledShares
  );
  const { state, filledPercent } = resolveFilledPercentAndState(
    childQuantity,
    childFilledShares,
    false
  );
  return { filledShares: childFilledShares, filledPercent, state };
}

export function buildChildTradeRow(parentRow, childTrade, childIndex, options = {}) {
  const { flatView = false } = options;
  const parentTotal =
    parentRow.totalOrderedShares ?? parentRow.quantity ?? 0;
  const { filledShares, filledPercent, state } = computeProRataChildFillMetrics(
    parentRow.filledShares ?? 0,
    parentTotal,
    childTrade.sharesOrdered ?? 0,
    parentRow.broker === "cros"
  );

  const parentId = parentRow.id ?? parentRow.tradeId;
  // Stable, unique grid id — must never collide with any top-level tradeId
  const childId = [
    "child",
    String(parentId),
    String(childIndex),
    String(childTrade.account ?? ""),
    String(childTrade.fund ?? ""),
    String(childTrade.strategy ?? ""),
  ].join("::");

  return {
    ...parentRow,
    id: childId,
    isChildRow: true,
    parentTradeId: parentId,
    rowNumber: undefined,
    serialNumber: undefined,
    __treeOrder: undefined,
    timestamp: childTrade.timestamp || parentRow.timestamp,
    distributionType: "C",
    fund: childTrade.fund,
    strategy: childTrade.strategy,
    quantity: childTrade.sharesOrdered,
    custodian: childTrade.custodian || parentRow.custodian,
    account: childTrade.account,
    filledShares,
    filledPercent,
    state,
    childTrades: undefined,
    distributionLegs: flatView ? undefined : parentRow.distributionLegs,
    onEditClick: undefined,
    onManageFillsClick: undefined,
  };
}

/** Expand parent trades into standalone child rows for flat grid view. */
export function flattenTradeRowsForGrid(rows) {
  const result = [];
  rows.forEach((row) => {
    if (row.distributionType === "P" && row.childTrades?.length > 0) {
      row.childTrades.forEach((childTrade, childIndex) => {
        result.push(
          buildChildTradeRow(row, childTrade, childIndex, { flatView: true })
        );
      });
      return;
    }
    result.push(row);
  });
  return result;
}

/**
 
 */
export function expandTradesForSummaryFilters(rows, { fund = "", strategy = "" } = {}) {
  const hasFund = Boolean(fund);
  const hasStrategy = Boolean(strategy);
  if (!hasFund && !hasStrategy) return rows || [];

  const result = [];
  (rows || []).forEach((row) => {
    // Parent: only matching distribution legs (shareCount + pro-rata fill)
    if (row?.distributionType === "P") {
      (row.childTrades || []).forEach((childTrade, childIndex) => {
        if (hasFund && childTrade.fund !== fund) return;
        if (hasStrategy && childTrade.strategy !== strategy) return;
        result.push(
          buildChildTradeRow(row, childTrade, childIndex, { flatView: true })
        );
      });
      return;
    }

    // Single: unchanged if it matches the filter
    if (hasFund && row?.fund !== fund) return;
    if (hasStrategy && row?.strategy !== strategy) return;
    result.push(row);
  });
  return result;
}

export function buildTradeTypeDisplay(action, position, isOptionTrade) {
  let tradeType = "";
  let isOpeningPosition = false;

  if (isOptionTrade) {
    if (action === "Buy" && position === "Long") {
      tradeType = "BUY TO OPEN";
      isOpeningPosition = true;
    } else if (action === "Sell" && position === "Long") {
      tradeType = "SELL TO CLOSE";
      isOpeningPosition = false;
    } else if (action === "Sell" && position === "Short") {
      tradeType = "SELL TO OPEN";
      isOpeningPosition = true;
    } else if (action === "Buy" && position === "Short") {
      tradeType = "BUY TO CLOSE";
      isOpeningPosition = false;
    } else {
      tradeType = `${action} ${position}`;
      isOpeningPosition = false;
    }
  } else if (action === "Buy" && position === "Long") {
    tradeType = "BUY LONG";
    isOpeningPosition = true;
  } else if (action === "Sell" && position === "Short") {
    tradeType = "SELL SHORT";
    isOpeningPosition = true;
  } else if (action === "Buy" && position === "Short") {
    tradeType = "COVER SHORT";
    isOpeningPosition = false;
  } else if (action === "Sell" && position === "Long") {
    tradeType = "SELL LONG";
    isOpeningPosition = false;
  } else {
    tradeType = `${action} ${position}`;
    isOpeningPosition = action === "Buy";
  }

  return { tradeType, isOpeningPosition };
}

export function isCommissionEligibleTrade(item, selectedDate, brokers) {
  const lastOrder = getLastOrder(item);
  if (!lastOrder) return false;
  const brokerAbb = findBrokerAbb(lastOrder.broker, brokers);
  if (brokerAbb == null || brokerAbb === "cros") return false;
  const lastOrderDate = new Date(lastOrder.date);
  const lastOrderDateUTC = Date.UTC(
    lastOrderDate.getUTCFullYear(),
    lastOrderDate.getUTCMonth(),
    lastOrderDate.getUTCDate()
  );
  const selectedDateUTC = Date.UTC(
    selectedDate.getFullYear(),
    selectedDate.getMonth(),
    selectedDate.getDate()
  );
  return lastOrderDateUTC === selectedDateUTC;
}

export function buildCommissionPayloadEntry(trade, brokers) {
  const lastOrder = getLastOrder(trade);
  const brokerAbb = findBrokerAbb(lastOrder.broker, brokers);
  const brokerLower = brokerAbb.toLowerCase();
  let commission = 0;
  if (brokerLower === "gspt") {
    commission = 0.01;
  } else if (brokerLower === "cfr") {
    commission = 0.0065;
  }
  return { trade: trade.tradeId, commission };
}
